<template>
  <div class="domain-control column w-[100vw] h-[100vh] relative">
    <div
      class="absolute bottom-2 left-4 z-[999] rounded-lg border border-vp-border bg-vp-secondary/80 p-1 backdrop-blur-sm pointer-events-auto"
    >
      <q-btn
        flat
        dense
        size="sm"
        class="!text-vp-accent min-w-9 min-h-9"
        icon="settings"
        @click="openSetting = true"
      />
    </div>
    <div
      v-show="showScreenshotFab"
      class="absolute left-4 top-2 z-[999] rounded-lg border border-vp-border bg-vp-secondary/80 p-1 backdrop-blur-sm pointer-events-auto"
    >
      <q-btn
        flat
        dense
        size="sm"
        class="!text-vp-accent min-w-9 min-h-9"
        icon="photo_camera"
        @click="handleScreenshotShare"
      />
    </div>
    <DebugPanels />
    <router-view v-if="showView"></router-view>
    <Setting v-model="openSetting" />
    <ScreenshotBlobQrCode ref="screenshotBlobQrCodeRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import { useQuasar } from "quasar";
import { menuList } from "./helper";
import { useDomainControlStore } from "@/stores/domainControl";
import Setting from "./components/Setting.vue";
import DebugPanels from "./components/DebugPanels.vue";
import ScreenshotBlobQrCode from "./components/ScreenshotBlobQrCode.vue";
import { useRoute } from "vue-router";
import { isWeb } from "@/utils/capacitor";
import VConsole from "vconsole";

const route = useRoute();
const showFullscreenIcon = computed(() => true);
const $q = useQuasar();
const domainControlStore = useDomainControlStore();

const openSetting = ref(false);
const showScreenshotFab = ref(true);
const screenshotBlobQrCodeRef = ref<{
  captureAndUpload: () => Promise<void>;
} | null>(null);
const showView = ref(true);
const vConsole = ref<VConsole | null>(null);

// theme
function applyTheme(theme: string) {
  $q.dark.set(theme === "dark");
}

const handleThemeChange = (e: MediaQueryListEvent) => {
  applyTheme(e.matches ? "dark" : "light");
};

let themeMediaQuery: MediaQueryList | null = null;

const watchThemeChange = () => {
  themeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  applyTheme(themeMediaQuery.matches ? "dark" : "light");
  themeMediaQuery.addEventListener("change", handleThemeChange);
};
// screen size（含 2880×1360 等大屏适配）
const handleSetScreenSize = () => {
  if (isWeb) return;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  let width: number;
  const height = 807;

  if (screenWidth >= 2800 && screenWidth <= 2900) {
    width = 2000;
    console.log("[LOG] 检测到大尺寸屏幕 2880 × 1360");
  } else if (screenWidth < 1900) {
    width = 1291;
  } else {
    width = 1400;
  }

  const viewport = document.querySelector('meta[name="viewport"]');

  console.log("[LOG] screenWidth:", screenWidth);
  console.log("[LOG] screenHeight:", screenHeight);
  console.log("[LOG] baseWidth:", width);

  let scale: number;
  if (screenWidth >= 2800 && screenWidth <= 2900) {
    scale = Math.min(screenWidth / width, screenHeight / height);
  } else {
    scale =
      screenWidth < 1900
        ? Math.min(screenWidth / width)
        : Math.min(screenWidth / width, screenHeight / height);
  }
  console.log("[BOWEN_LOG] 🚀 ~~ onMounted ~~ scale:", scale);
  viewport?.setAttribute(
    "content",
    "width=device-width, initial-scale=" + scale
  );
};

const setupVConsole = () => {
  vConsole.value = new VConsole();
};

onMounted(() => {
  handleSetScreenSize();
  watchThemeChange();
});
onUnmounted(() => {
  themeMediaQuery?.removeEventListener("change", handleThemeChange);
  themeMediaQuery = null;
  vConsole.value?.destroy();
});

// 与 beforeRouteUpdate 相同，无法访问 `this`
// onBeforeRouteUpdate(async (to, from) => {
//   console.log("[BOWEN_LOG] 🚀 ~~ onBeforeRouteUpdate ~~ to, from:", to, from);
//   //仅当 id 更改时才获取用户，例如仅 query 或 hash 值已更改
// });

const handleResetView = () => {
  showView.value = false;
  setTimeout(() => {
    // 给 unsubscribeAll 留时间
    showView.value = true;
  }, 500);
};

const handleScreenshotShare = async () => {
  showScreenshotFab.value = false;
  await nextTick();
  $q.loading.show({ message: "正在截图并上传…" });
  try {
    await screenshotBlobQrCodeRef.value?.captureAndUpload();
  } finally {
    $q.loading.hide();
    showScreenshotFab.value = true;
  }
};

watch(
  () => $q.dark.isActive,
  (val) => {
    console.log(val ? "On dark mode" : "On light mode");
    document.body.classList.toggle("dark", val);
    handleResetView();
  }
);

watch(
  () => domainControlStore.showVConsole,
  (val) => {
    if (val) {
      if (vConsole.value) {
        vConsole.value.showSwitch();
      } else {
        setupVConsole();
      }
    } else {
      vConsole.value?.hideSwitch();
    }
  },
  {
    immediate: true,
  }
);
</script>

<style lang="scss">
// 通用样式修改 防止 tailwind 样式影响 q-icon 的行高
.q-icon {
  line-height: 1 !important;
}
.q-notification {
  border-radius: 15px !important;
}
.q-chip {
  border-radius: 8px !important;
}
.select-chip {
  background-color: rgba(129, 138, 146, 0.4);
  color: #fff;
  // padding: 0 10px;
}
.select-chip.q-chip.disabled {
  opacity: 0.6 !important;
}
.select-chip.q-chip--selected {
  background-color: #4caf50;
}

// 并且不是 q-btn--round
.q-btn:not(.q-btn--round) {
  border-radius: 8px !important;
}

/* 禁用长按菜单 */
* {
  -webkit-touch-callout: none; /* 禁用Safari上的长按菜单 */
  -webkit-user-select: none; /* 禁用Safari上的文本选择 */
  -moz-user-select: none; /* 禁用Firefox上的文本选择 */
  -ms-user-select: none; /* 禁用IE上的文本选择 */
  user-select: none; /* 禁用其他浏览器上的文本选择 */
}

#__vconsole {
  font-size: 14px !important;
}

.btn-style {
  background: radial-gradient(
    circle at center,
    rgba(252, 251, 251, 0.8) 0%,
    /* 中心发光 */ rgb(176, 183, 193) 70%,
    /* 渐变过渡 */ rgb(156, 158, 161) 100% /* 边缘颜色 */
  ) !important;
}
.btn-active {
  background: radial-gradient(
    circle at center,
    rgba(247, 194, 145, 0.8) 0%,
    /* 中心发光 */ rgb(215, 153, 82) 40%,
    /* 渐变过渡 */ rgb(224, 101, 1) 100% /* 边缘颜色 */
  ) !important;
}

.chart-tip {
  font-weight: 700;
  font-size: 18px;
  padding: 5px;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
}
.domain-control {
  // background-color: #d1d9e2 ;
  // border-radius: 20px !important;
  // overflow: hidden;
  height: 100vh; /* 确保高度覆盖整个视口 */
  width: 100vw; /* 确保宽度覆盖整个视口 */
  overflow: hidden; /* 防止溢出 */
}
.domain-control .q-card {
  border-radius: 20px;
  // box-shadow: 0 1px 13px 7px rgba(0, 0, 0, 0.2);
  // background: radial-gradient(
  //   circle,
  //   rgb(250 253 255) 0%,
  //   rgb(162 171 182) 100%
  // );
  border: 0;
  // background: #ffffff;
  box-shadow: none;
}
// .gradient-text {
//   background: linear-gradient(to right, rgb(78 104 121), rgb(247 112 0));
//   -webkit-background-clip: text; /*将设置的背景颜色限制在文字中*/
//   -webkit-text-fill-color: transparent; /*给文字设置成透明*/
// }

// body {
//   background-color: transparent !important;
// }

.body--light {
  .q-tabs {
    background-color: #d1d9e2 !important;
  }
  .q-tab {
    background-color: #d1d9e2 !important;
  }
  .q-tab--active {
    background-color: #fff !important;
  }
  .domain-control {
    background-color: #e6e6e6 !important;
  }
  .q-card {
    background-color: #d1d9e2 !important;
  }
  .q-img__content > div {
    color: #000;
  }
  .slider-color {
    color: #032f2b;
  }
}
.body--dark {
  .q-tabs {
    background-color: #292d32 !important;
  }
  .q-tab {
    background-color: #292d32 !important;
  }
  .q-tab--active {
    background-color: #000 !important;
  }
  .domain-control {
    background-color: #000;
  }
  .q-card {
    background-color: #292d32 !important;
  }
  .q-img__content > div {
    color: #ffffff;
  }
  .slider-color {
    color: #ffffff;
  }
}
</style>
<style scoped lang="scss">
.q-tabs {
  // background-color: #fff !important;
  text-transform: none;
  border-radius: 20px !important;
  margin: 0 16px;
  // margin-bottom: 10px;
  padding: 5px;
  padding-left: 60px; // 底部与左上角截图按钮错开
}
.q-tab {
  // background-color: #fff !important;
  text-transform: none;
  border-radius: 10px !important;
  /* margin: 0 20px; */
}
// .q-tab--active {
//   /* background: radial-gradient(circle, #e6ebef, #8e96a1) !important; */
//   background-color: #d1d9e2  !important;
// }
:deep(.q-tab__indicator) {
  display: none;
}
:deep(.q-tab__label) {
  font-size: 18px;
  font-weight: 700;
}

// :deep(.q-img__content > div) {
//   color: #000;
// }
/* .q-layout-container { */

/* } */

:deep(.q-linear-progress__stripe) {
  animation: progress-linear-stripes 10s infinite linear;
}

@keyframes progress-linear-stripes {
  0% {
    background-position-x: 1000px;
  }
}
</style>
